package com.mgacreative.hotel.feature.restaurant.ui

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.mgacreative.hotel.domain.model.PosCategory
import com.mgacreative.hotel.domain.model.PosItem
import com.mgacreative.hotel.domain.model.Reservation
import com.mgacreative.hotel.domain.repository.PosRepository
import com.mgacreative.hotel.domain.repository.ReservationRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

import com.mgacreative.hotel.domain.repository.InventoryRepository

class PosViewModel(
    private val posRepository: PosRepository,
    private val reservationRepository: ReservationRepository,
    private val inventoryRepository: InventoryRepository? = null
) : ViewModel() {

    val categories: StateFlow<List<PosCategory>> = posRepository.observeCategories()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val items: StateFlow<List<PosItem>> = posRepository.observeItems()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    val openOrders: StateFlow<List<com.mgacreative.hotel.domain.model.PosOrder>> = posRepository.observeOpenOrders()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), emptyList())

    private val _cart = MutableStateFlow<Map<PosItem, Int>>(emptyMap())
    val cart: StateFlow<Map<PosItem, Int>> = _cart.asStateFlow()

    private val _currentOrderId = MutableStateFlow<String?>(null)
    val currentOrderId: StateFlow<String?> = _currentOrderId.asStateFlow()

    private val _activeReservations = MutableStateFlow<List<Reservation>>(emptyList())
    val activeReservations: StateFlow<List<Reservation>> = _activeReservations.asStateFlow()

    private val _paymentState = MutableStateFlow<PaymentState>(PaymentState.Idle)
    val paymentState: StateFlow<PaymentState> = _paymentState.asStateFlow()

    init {
        loadData()
    }

    private fun loadData() {
        viewModelScope.launch {
            posRepository.loadMenu()
            posRepository.loadOpenOrders()
            val reservations = reservationRepository.getReservations().filter {
                val s = it.status.uppercase()
                s == "CHECKED_IN" || s == "CHECK_IN" || s == "CONFIRMED" || s.isEmpty()
            }
            _activeReservations.value = reservations
        }
    }

    fun addToCart(item: PosItem) {
        val currentCart = _cart.value.toMutableMap()
        val currentQty = currentCart[item] ?: 0
        currentCart[item] = currentQty + 1
        _cart.value = currentCart
    }

    fun addManualItem(name: String, price: Double) {
        val manualItem = PosItem(id = "", tenant_id = "", category_id = "", name = name, price = price)
        addToCart(manualItem)
    }

    fun removeFromCart(item: PosItem) {
        val currentCart = _cart.value.toMutableMap()
        val currentQty = currentCart[item] ?: 0
        if (currentQty > 1) {
            currentCart[item] = currentQty - 1
        } else {
            currentCart.remove(item)
        }
        _cart.value = currentCart
    }

    fun updateQuantity(item: PosItem, newQuantity: Int) {
        val currentCart = _cart.value.toMutableMap()
        if (newQuantity <= 0) {
            currentCart.remove(item)
        } else {
            currentCart[item] = newQuantity
        }
        _cart.value = currentCart
    }

    fun removeCartItemCompletely(item: PosItem) {
        val currentCart = _cart.value.toMutableMap()
        currentCart.remove(item)
        _cart.value = currentCart
    }

    fun clearCart() {
        _cart.value = emptyMap()
        _currentOrderId.value = null
    }

    fun saveCurrentOrder(tableName: String) {
        if (_cart.value.isEmpty()) return
        viewModelScope.launch {
            val totalAmount = _cart.value.entries.sumOf { it.key.price * it.value }
            val newId = posRepository.saveOrder(_currentOrderId.value, tableName, _cart.value, totalAmount)
            if (newId != null) {
                _currentOrderId.value = newId
            }
        }
    }

    fun createNewTableOrder(tableName: String, onCreated: () -> Unit) {
        viewModelScope.launch {
            _cart.value = emptyMap()
            _currentOrderId.value = null
            val newId = posRepository.saveOrder(null, tableName, emptyMap(), 0.0)
            if (newId != null) {
                _currentOrderId.value = newId
            }
            onCreated()
        }
    }

    fun loadOrder(order: com.mgacreative.hotel.domain.model.PosOrder) {
        viewModelScope.launch {
            val orderItems = posRepository.getOrderItems(order.id)
            val newCart = mutableMapOf<PosItem, Int>()
            orderItems.forEach { orderItem ->
                val itemId = orderItem.item_id
                val posItem = if (itemId == null) {
                    PosItem(id = "", category_id = "", name = orderItem.manual_name ?: "Manuel Ürün", price = orderItem.price_at_time)
                } else {
                    items.value.find { it.id == itemId } 
                        ?: PosItem(id = itemId, category_id = "", name = "Bilinmeyen Ürün", price = orderItem.price_at_time)
                }
                newCart[posItem] = orderItem.quantity
            }
            _cart.value = newCart
            _currentOrderId.value = order.id
        }
    }

    fun takePayment(tableName: String, reservationId: String? = null) {
        if (_cart.value.isEmpty()) return
        
        viewModelScope.launch {
            _paymentState.value = PaymentState.Loading
            val totalAmount = _cart.value.entries.sumOf { it.key.price * it.value }
            val currentCartValues = _cart.value.toMap() // Snapshot of cart
            
            val success = posRepository.takePayment(_currentOrderId.value, tableName, _cart.value, totalAmount, reservationId)
            if (success) {
                // Auto-deduct inventory
                if (inventoryRepository != null) {
                    try {
                        inventoryRepository.loadInventoryItems()
                        val allInvItems = inventoryRepository.observeInventoryItems().first()
                        currentCartValues.forEach { (posItem, qty) ->
                            val invItem = allInvItems.find { it.name.equals(posItem.name, ignoreCase = true) }
                            if (invItem != null) {
                                inventoryRepository.addTransaction(
                                    itemId = invItem.id,
                                    type = "REDUCE",
                                    quantity = qty,
                                    description = "POS Satışı ($tableName)"
                                )
                            }
                        }
                    } catch (e: Exception) {
                        e.printStackTrace()
                    }
                }
                
                _paymentState.value = PaymentState.Success
                clearCart()
            } else {
                _paymentState.value = PaymentState.Error("Ödeme alınamadı. Lütfen tekrar deneyin.")
            }
        }
    }
    
    fun resetPaymentState() {
        _paymentState.value = PaymentState.Idle
    }
}

sealed class PaymentState {
    object Idle : PaymentState()
    object Loading : PaymentState()
    object Success : PaymentState()
    data class Error(val message: String) : PaymentState()
}
